import { useEffect, useRef, useState, type CSSProperties } from 'react'
import Markdown, { type Components } from 'react-markdown'
import { AlertCircle, RotateCcw } from 'lucide-react'
import {
  BUI_EASE,
  Button,
  prefersReducedMotion,
  ThinkingState,
  ToolChips,
} from '@loop3/ui'
import { EntityGrid } from '@/components/entity-card'
import { dedupeTeamPersons } from '@/lib/compute/universe'
import type { ChatTurn } from '@/lib/types/universe'

const PROSE =
  'max-w-[68ch] text-[0.9375rem] leading-7 text-foreground [overflow-wrap:anywhere] ' +
  '[&>*+*]:mt-3 [&_h1]:font-display [&_h1]:text-xl [&_h2]:font-display [&_h2]:text-lg [&_h3]:font-medium ' +
  '[&_h1,&_h2,&_h3]:mt-5 [&_h1,&_h2,&_h3]:leading-snug [&_strong]:font-semibold ' +
  '[&_ul]:list-disc [&_ol]:list-decimal [&_ul,&_ol]:space-y-1 [&_ul,&_ol]:pl-5 [&_li]:marker:text-muted-foreground ' +
  '[&_:not(pre)>code]:rounded [&_:not(pre)>code]:bg-muted [&_:not(pre)>code]:px-1 [&_:not(pre)>code]:font-mono [&_:not(pre)>code]:text-[0.85em] ' +
  '[&_pre]:overflow-x-auto [&_pre]:rounded-lg [&_pre]:bg-muted [&_pre]:p-3 [&_pre]:font-mono [&_pre]:text-xs ' +
  '[&_blockquote]:border-l-2 [&_blockquote]:border-border [&_blockquote]:pl-3 [&_blockquote]:text-muted-foreground ' +
  '[&_hr]:border-border'

const MARKDOWN: Components = {
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="font-medium text-[var(--l3-violet)] underline decoration-[var(--l3-violet)]/40 underline-offset-2 hover:decoration-[var(--l3-violet)]"
    >
      {children}
    </a>
  ),
}

const REVEAL_MS = 900

/** Characters of `text` revealed so far. Progress follows elapsed time, so throttled timers cannot stretch it; a hidden tab shows everything. */
function useReveal(text: string, animate: boolean) {
  const [shown, setShown] = useState(() =>
    animate && !prefersReducedMotion() && !document.hidden ? 0 : text.length
  )
  useEffect(() => {
    if (shown >= text.length) return
    const start = performance.now()
    const tick = () => {
      const progress = document.hidden
        ? 1
        : Math.min(1, (performance.now() - start) / REVEAL_MS)
      setShown(Math.ceil(progress * text.length))
      if (progress === 1) stop()
    }
    const id = setInterval(tick, 16)
    document.addEventListener('visibilitychange', tick)
    const stop = () => {
      clearInterval(id)
      document.removeEventListener('visibilitychange', tick)
    }
    return stop
    // Runs once per mount: the reveal never restarts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  return shown
}

function AssistantTurn({ turn, fresh }: { turn: ChatTurn; fresh: boolean }) {
  const shown = useReveal(turn.text, fresh)
  const revealing = shown < turn.text.length
  const tools = turn.reply?.tools ?? []
  const entities = dedupeTeamPersons(turn.reply?.entities ?? [])

  return (
    <div className="flex flex-col gap-3">
      {tools.length > 0 && (
        // Labels only: tool identifiers are internal.
        <ToolChips tools={tools.map(({ label }) => ({ label }))} />
      )}
      <div className={PROSE}>
        <Markdown skipHtml disallowedElements={['img']} components={MARKDOWN}>
          {turn.text.slice(0, shown)}
        </Markdown>
      </div>
      {!revealing && entities.length > 0 && (
        <EntityGrid items={entities} className="md:grid-cols-2" />
      )}
    </div>
  )
}

function ErrorTurn({
  error,
  onRetry,
}: {
  error: string
  onRetry?: () => void
}) {
  return (
    <div
      role="alert"
      className="flex max-w-[68ch] flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border border-destructive/30 bg-destructive/5 px-3.5 py-2.5 text-sm"
    >
      <AlertCircle className="size-4 shrink-0 text-destructive" aria-hidden />
      <span className="min-w-0 flex-1">{error}</span>
      {onRetry && (
        <Button variant="outline" size="sm" onClick={onRetry}>
          <RotateCcw aria-hidden />
          Retry
        </Button>
      )}
    </div>
  )
}

/** The conversation: questions as bubbles, answers as Markdown over tool runs and entity cards. */
export function LupeThread({
  turns,
  pending,
  onRetry,
}: {
  turns: ChatTurn[]
  pending: boolean
  onRetry(): void
}) {
  // Turns restored from the session render statically; only answers arriving now type out.
  const restored = useRef(turns.length)
  const endRef = useRef<HTMLDivElement>(null)
  const last = turns[turns.length - 1]
  const latestAnswer =
    last?.role === 'assistant' && !last.error && turns.length > restored.current
      ? last.text
      : ''

  useEffect(() => {
    endRef.current?.previousElementSibling?.scrollIntoView({
      block: last?.role === 'assistant' && !pending ? 'start' : 'end',
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    })
    // Scroll only when the thread grows or starts waiting.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [turns.length, pending])

  return (
    <div className="flex flex-col gap-8">
      {turns.map((turn, i) => {
        const fresh = i >= restored.current
        const motion: CSSProperties | undefined = fresh
          ? { animation: `bui-fade-up 320ms ${BUI_EASE} both` }
          : undefined
        if (turn.role === 'user')
          return (
            <div
              key={i}
              className="bui-motion flex scroll-mt-20 scroll-mb-40 justify-end"
              style={motion}
            >
              <p className="max-w-[85%] rounded-2xl rounded-br-md bg-muted px-4 py-2.5 text-[0.9375rem] leading-6 whitespace-pre-wrap [overflow-wrap:anywhere]">
                {turn.text}
              </p>
            </div>
          )
        return (
          <div
            key={i}
            className="bui-motion scroll-mt-20 scroll-mb-40"
            style={motion}
          >
            {turn.error ? (
              <ErrorTurn
                error={turn.error}
                onRetry={
                  i === turns.length - 1 && !pending ? onRetry : undefined
                }
              />
            ) : (
              <AssistantTurn turn={turn} fresh={fresh} />
            )}
          </div>
        )
      })}
      {pending && (
        <div className="scroll-mt-20 scroll-mb-40">
          <ThinkingState label="Lupe is thinking" />
        </div>
      )}
      <div ref={endRef} aria-hidden />
      {/* Announces each new answer once, instead of every revealed character. */}
      <p className="sr-only" aria-live="polite">
        {latestAnswer && `Lupe answered: ${latestAnswer}`}
      </p>
    </div>
  )
}
